import { ReactNode, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { CButton } from './CButton';
import { CText } from './CText';
import { AnimatePresence, motion } from 'framer-motion';

const ModalPortal = ({ children }: { children: ReactNode }) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  if (typeof window === 'undefined' || !mounted) {
    return null;
  }

  const modalRoot = document.getElementById('modal-root');
  if (!modalRoot) {
    console.error("The element with id 'modal-root' was not found in the DOM.");
    return null;
  }

  return createPortal(children, modalRoot);
};

export default ModalPortal;

export function CModal(props: {
  title?: string;
  desc?: string;
  confirmText?: string;
  open: boolean;
  onClose?: () => void;
  onConfirm?: () => void;
  children?: ReactNode;
  'data-testid-close'?: string;
  'data-testid-confirm'?: string;
}) {
  return (
    <ModalPortal>
      <AnimatePresence>
        {props.open && (
          <motion.div
            className="fixed top-0 left-0 w-screen h-screen bg-[rgba(0,0,0,0.6)] backdrop-filter-[blur(5px)] z-2000
      flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => props.onClose && props.onClose()}
          >
            {props.children ? (
              props.children
            ) : (
              <div
                className="w-[348px] bg-[#F6F6F6] dark:bg-[#222222] rounded-[18px] p-[32px_16px_16px_16px] flex flex-col items-center gap-[30px]"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <div>
                  <CText type="H1">{props.title}</CText>
                </div>
                <div>
                  <CText type="body1" opacity={0.6}>
                    {props.desc}
                  </CText>
                </div>
                <div className="flex gap-[12px] w-full">
                  <CButton
                    size="full"
                    onClick={() => props.onClose && props.onClose()}
                    data-testid={props['data-testid-close']}
                  >
                    Cancel
                  </CButton>
                  <CButton
                    size="full"
                    color="modal"
                    onClick={() => props.onConfirm && props.onConfirm()}
                    data-testid={props['data-testid-confirm']}
                  >
                    {props.confirmText}
                  </CButton>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </ModalPortal>
  );
}
